import { Search, Menu, LogOut, ChevronsUpDown, Check, GraduationCap } from "lucide-react";
import { useGuide } from "@/lib/guide";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ManaWordmark } from "@/components/mh/Brand";
import { useAuth } from "@/context/AuthContext";

const WorkspaceSwitcher = () => {
  const { workspaces, current, isAdmin, switchWorkspace } = useAuth();
  if (!current) return null;
  if (!isAdmin) return <span className="hidden sm:inline font-mono text-[11px] text-muted-foreground" data-testid="workspace-name">{current.name}</span>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="inline-flex items-center gap-2 h-9 px-3 rounded-full border border-white/10 hover:border-white/30 text-sm max-w-[180px] sm:max-w-[240px]" data-testid="workspace-switcher">
          <span className="w-1.5 h-1.5 rounded-full bg-[#D4FF3A]" /><span className="truncate">{current.name}</span><ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 bg-[#0A0A0F] border-white/10">
        <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Switch workspace</DropdownMenuLabel>
        {workspaces.map((w) => (
          <DropdownMenuItem key={w.id} onClick={() => w.id !== current.id && switchWorkspace(w.id)} data-testid={`switch-ws-${w.slug}`}>
            <span className="flex-1 truncate">{w.name}</span>{w.id === current.id && <Check className="w-4 h-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const UserMenu = () => {
  const { user, role, isAdmin, logout } = useAuth();
  const nav = useNavigate();
  if (!user) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="w-9 h-9 rounded-full bg-white/[0.06] border border-white/10 grid place-items-center text-xs font-semibold uppercase" data-testid="user-menu">{(user.name || user.email)[0]}</button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 bg-[#0A0A0F] border-white/10">
        <DropdownMenuLabel><div className="text-sm truncate">{user.name}</div><div className="font-mono text-[10px] text-muted-foreground truncate">{user.email}</div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-[#D4FF3A] mt-1" data-testid="user-role">{isAdmin ? "Agency admin" : role}</div></DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-btn"><LogOut className="w-4 h-4 mr-2" />Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const LearnButton = () => {
  const { openGuide } = useGuide();
  return (
    <button onClick={() => openGuide("page")} data-testid="learn-btn" title="Learn how to use this page and the whole platform"
      className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-full border border-[#D4FF3A]/40 text-[#D4FF3A] text-sm hover:bg-[#D4FF3A]/10 transition-colors">
      <GraduationCap className="w-4 h-4" /><span className="hidden sm:inline">Learn</span>
    </button>
  );
};

export default function Header({ onMenuToggle }) {
  return (
    <header className="h-14 sm:h-16 border-b border-white/[0.07] bg-[#030305]/80 backdrop-blur-md sticky top-0 z-30 flex items-center justify-between gap-2 px-3 sm:px-4 md:px-6" data-testid="header">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        <Button variant="ghost" size="icon" onClick={onMenuToggle} className="hidden lg:flex h-9 w-9" data-testid="menu-toggle"><Menu className="w-5 h-5" strokeWidth={1.5} /></Button>
        <div className="lg:hidden"><ManaWordmark compact /></div>
        <WorkspaceSwitcher />
      </div>
      <div className="flex items-center gap-1.5 sm:gap-2">
        <div className="relative hidden md:block">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input placeholder="Search the atlas…" onKeyDown={(e) => e.key === "Enter" && window.location.assign(`/atlas?q=${encodeURIComponent(e.currentTarget.value)}`)}
            className="pl-10 w-48 lg:w-64 h-9 rounded-full bg-white/[0.04] border border-white/10 text-sm outline-none focus:border-white/30" data-testid="header-search" />
        </div>
        <LearnButton />
        <UserMenu />
      </div>
    </header>
  );
}
